import { Button, Classes, Dialog, FileInput, FormGroup, HTMLTable, InputGroup, Tab, Tabs } from "@blueprintjs/core";
import { MDBBtn, MDBCol, MDBRow } from "mdbreact";
import { observable } from "mobx";
import { observer } from "mobx-react";
import * as React from "react";
import { IProduto } from "../../model";
import { AppStore } from "../../stores/AppStore";
import { ProdutoStore } from "../../stores/legislacao/ProdutoStore";
import { Pager } from "../utils/Pager";
import { ProdutoDialog } from "./ProdutoDialog";
import { ProdutoDialogCreate } from "./ProdutoDialogCreate";
import moment from "moment";
var debounce = require('lodash.debounce');

interface ProdutosPageProps {
  estado: string;
}

interface ProdutosPageState { }

@observer
export class ProdutosPage extends React.Component<ProdutosPageProps, ProdutosPageState> {

  obs = observable({
    create: false as boolean,
    viewing: null as IProduto,
    currentPage: 1 as number,
    loading_export: false as boolean,
    loading_import: false as boolean,
    open_import_produtos: false as boolean,
    file_name: "Selecione um arquivo XLSX" as string,
    fetchOptions: {} as any
  })

  constructor(props: ProdutosPageProps) {
    super(props);

    this.obs.fetchOptions = debounce(this.handleFilter.bind(this), 800);

  }

  async componentDidMount() {

    AppStore.setTitle("Produtos");

    await ProdutoStore.loadProdutos(1, "", "entrada", this.props.estado);

  }

  handleFilter = async () => {

    await ProdutoStore.loadProdutos(1, ProdutoStore.obs.filter, ProdutoStore.obs.selectedTab,this.props.estado)

  };

  /**
  * Define o filtro de produtos e reinicia a listagem na primeira página.
  *
  * A função:
  * - Atualiza `ProdutoStore.obs.filter` com o valor digitado pelo usuário.
  * - Reinicia a paginação local (`obs.currentPage = 1`).
  * - Chama `obs.fetchOptions()` para buscar os produtos filtrados.
  *
  * @param {any} e Evento do input que contém o novo valor do filtro.
  *
  * @returns {void} Não retorna valor.
  */

  handleSetFilter = async (e: any) => {

    ProdutoStore.obs.filter = e.target.value

    this.obs.currentPage = 1;

    this.obs.fetchOptions()

  };

  handleView(produto: IProduto) {
    
    produto.valido_de = produto.valido_de ? moment(produto.valido_de).add(3, "hours").toDate() : null
    produto.valido_ate = produto.valido_ate ? moment(produto.valido_ate).add(3, "hours").toDate() : null

    this.obs.viewing = produto;
  }

  handleCreate = () => {
    this.obs.create = true
  }

  /**
  * Exclui um produto após confirmação do usuário e recarrega a listagem.
  *
  * A função:
  * - Exibe um `window.confirm` solicitando confirmação para excluir o produto.
  * - Se confirmado:
  *   - Define o `estado` do produto com base na prop `this.props.estado`.
  *   - Chama `ProdutoStore.handleDelete(produto)` para excluir o item.
  *   - Se a exclusão for bem-sucedida:
  *     - Reseta a página atual para 1 (`obs.currentPage = 1`).
  *     - Chama `ProdutoStore.loadProdutos` para recarregar a listagem com os filtros atuais.
  *
  * @async
  * @param {Object} produto Objeto do produto a ser excluído. Deve conter, no mínimo, a propriedade `descricao`.
  *
  * @returns {Promise<void>} Promise resolvida após tentativa de exclusão e recarregamento da lista.
  */

  async handleDelete(produto) {

    if (window.confirm(`Excluir produto ${produto.descricao}`)) {

      produto.estado  = this.props.estado

      let res = await ProdutoStore.handleDelete(produto)

      if (res) {

        this.obs.currentPage = 1;

        await ProdutoStore.loadProdutos(1, ProdutoStore.obs.filter, ProdutoStore.obs.selectedTab, this.props.estado)

      }

    }

  }

  handleViewClose = () => {
    this.obs.viewing = null;
  };

  /**
  * Inicializa a inclusão de um novo produto.
  *
  * A função:
  * - Cria um objeto padrão representando um novo produto.
  * - Define este objeto em `this.obs.viewing`, habilitando a tela/modal de edição.
  *
  * ## Valores padrão do novo produto
  * - `descricao`: `"Novo produto"`
  * - `cest`: `"X"` (placeholder até preenchimento do usuário)
  * - `ncm`: `""` (vazio)
  * - `aliquota_interna`: `17` (valor inicial padrão)
  * - `aliquota_interna_regime`: `null`
  * - `entrada`: `true` (indica operação de entrada por padrão)
  * - `estado`: valor atual de `this.props.estado`
  * - `seg_auto_pecas`: `false`
  * - `seg_vet_pet`: `false`
  *
  * @returns {void}
  */

  handleAdd = () => {

    this.obs.viewing = {
      descricao: "Novo produto",
      cest: "X",
      ncm: "",
      aliquota_interna: 17,
      aliquota_interna_regime: null,
      entrada: true,
      estado: this.props.estado,
      seg_auto_pecas: false,
      seg_vet_pet: false,
      seg_refrigerantes: false,
      seg_agua_out: false,
      seg_lamp_reatores: false,
      seg_mat_limpeza: false,
      seg_pneumaticos_prot: false,
      seg_alimenticios: false,
      seg_perf_cosm: false,
      seg_eletro: false,
      seg_racoes: false,
      seg_sorvetes_fab: false,
      seg_tintas: false,
      seg_mat_construcao: false,
      seg_veiculos_automotores: false,
      seg_veiculos_novos: false,
      seg_medicamentos_humano: false,
      valido_de: null,
      valido_ate: null
    };

  };

  /**
  * Exporta os produtos com base na aba selecionada e no estado atual.
  *
  * A função:
  * - Ativa o estado de carregamento (`obs.loading_export = true`).
  * - Chama `ProdutoStore.handleExport`, passando:
  *   - A aba selecionada (`ProdutoStore.obs.selectedTab`), por exemplo `"entrada"` ou `"saida"`.
  *   - O estado atual (`this.props.estado`).
  * - Após a exportação, desativa o estado de carregamento.
  *
  * @async
  * @returns {Promise<void>} Promise resolvida após a conclusão da exportação.
  */

  async handleExport() {
    this.obs.loading_export = true

    await ProdutoStore.handleExport(ProdutoStore.obs.selectedTab, this.props.estado)

    this.obs.loading_export = false
  }

  /**
  * Abre o modal de importação de produtos.
  *
  * A função:
  * - Define `obs.open_import_produtos` como `true`, permitindo que o modal de importação seja exibido na interface.
  *
  * @returns {void} Não retorna valor.
  */

  handleImport() {

    this.obs.open_import_produtos = true;

  };

  /**
  * Lida com a importação de produtos via arquivo CSV no contexto do estado atual.
  *
  * A função:
  * - Ativa o estado de carregamento (`obs.loading_import = true`).
  * - Captura o primeiro arquivo do input (`event.target.files[0]`).
  * - Atualiza o nome do arquivo em `obs.file_name`.
  * - Cria um `FormData` com os seguintes dados:
  *   - `"file"`: o arquivo selecionado.
  *   - `"entrada"`: `"true"` se a aba selecionada for `"entrada"`, `"false"` caso contrário.
  *   - `"estado"`: valor de `this.props.estado`.
  * - Envia os dados para `ProdutoStore.handleImport(data)` para processar a importação.
  * - Se a importação for bem-sucedida:
  *   - Reseta a página atual para 1.
  *   - Chama `ProdutoStore.loadProdutos` com os filtros e estado atuais para recarregar a listagem.
  *   - Fecha o modal de importação (`obs.open_import_produtos = false`).
  *   - Restaura o texto do seletor de arquivo para o valor padrão.
  * - Desativa o estado de carregamento (`obs.loading_import = false`).
  *
  * @async
  * @param {Event} event Evento de alteração do input de arquivo.
  *
  * @returns {Promise<void>} Promise resolvida após a tentativa de importação e atualização da listagem.
  */

  handleFileInputChange = async (event) => {

    this.obs.loading_import = true;

    const file = event.target.files[0];

    this.obs.file_name = file.name

    var data = new FormData();
    data.append("file", file);
    data.append("entrada", ProdutoStore.obs.selectedTab == "entrada" ? "true" : "false");
    data.append("estado", this.props.estado);

    let res = await ProdutoStore.handleImport(data)

    if (res) {

      this.obs.currentPage = 1;

      await ProdutoStore.loadProdutos(1, ProdutoStore.obs.filter, ProdutoStore.obs.selectedTab, this.props.estado)

      this.obs.open_import_produtos = false;

      this.obs.file_name = "Selecione um arquivo CSV"

    }

    this.obs.open_import_produtos = false;

    this.obs.loading_import = false;

  };

  /**
  * Formata uma data de validade para o padrão brasileiro `DD/MM/YYYY`.
  *
  * A função:
  * - Recebe uma data (`d`) em qualquer formato aceito pelo **moment.js**.
  * - Adiciona 3 horas ao valor original (ajuste de fuso/servidor).
  * - Retorna a data formatada no padrão `DD/MM/YYYY`.
  * - Caso `d` seja `null` ou `undefined`, retorna `null`.
  *
  * @param {string|Date|number|null|undefined} d Data a ser formatada.
  * Aceita string em formato ISO, objeto `Date` ou timestamp numérico.
  *
  * @returns {string|null} Data formatada como `DD/MM/YYYY`, ou `null` se não houver data.
  */

  validadeFormatter(d) {
      return d ? moment(d).add(3, "hour").format("DD/MM/YYYY") : null;
    }

  /**
  * Altera a aba selecionada e recarrega a listagem de produtos com os filtros aplicados.
  *
  * A função:
  * - Atualiza `ProdutoStore.obs.selectedTab` com o novo `tabId`.
  * - Chama `ProdutoStore.loadProdutos`, passando:
  *   - Página 1.
  *   - O filtro atual (`ProdutoStore.obs.filter`).
  *   - A nova aba (`tabId`).
  *   - O estado atual (`this.props.estado`).
  *
  * @async
  * @param {string} tabId ID da aba selecionada (por exemplo: `"entrada"` ou `"saida"`).
  *
  * @returns {Promise<void>} Promise resolvida após o carregamento da nova listagem.
  */

  async handleChangeTab(tabId) {

    ProdutoStore.obs.selectedTab = tabId

    await ProdutoStore.loadProdutos(1, ProdutoStore.obs.filter, tabId, this.props.estado)

  }

  /**
  * Altera a página atual da listagem de produtos e recarrega os dados.
  *
  * A função:
  * - Atualiza `obs.currentPage` com o número da nova página.
  * - Chama `ProdutoStore.loadProdutos`, passando:
  *   - A nova página (`page`).
  *   - O filtro atual (`ProdutoStore.obs.filter`).
  *   - A aba selecionada (`ProdutoStore.obs.selectedTab`).
  *   - O estado atual (`this.props.estado`).
  *
  * @async
  * @param {number} page Número da página a ser carregada.
  *
  * @returns {Promise<void>} Promise resolvida após o carregamento da nova página.
  */

  async handleChangePage(page) {

    this.obs.currentPage = page;

    await ProdutoStore.loadProdutos(page, ProdutoStore.obs.filter, ProdutoStore.obs.selectedTab, this.props.estado)

  }

  render() {

    let user_admin = AppStore.obs1.usuario ? AppStore.obs1.usuario.admin : false

    let items = ProdutoStore.obs.produtos?.map(produto => {
      return (
        <tr key={produto.id}>
          <td>{produto.cest}</td>
          <td>{produto.ncm}</td>
          <td>{produto.descricao}</td>
          <td>{this.validadeFormatter(produto.valido_de)}</td>
          <td>{this.validadeFormatter(produto.valido_ate)}</td>
          {user_admin &&
            <td className="nz-table-actions">
              <MDBBtn
                color="default"
                rounded
                size="sm"
                onClick={this.handleView.bind(this, produto)}
              >
                <img src="/images/edit.png" width={25} />
              </MDBBtn>
              <MDBBtn
                color="default"
                rounded
                size="sm"
                onClick={this.handleDelete.bind(this, produto)}
              >
                <img src="/images/bin.png" width={25} />
              </MDBBtn>
            </td>
          }
        </tr>
      );
    });

    return (
      <div>
        <MDBRow>
          <MDBCol md="12">

            {user_admin &&
              <MDBBtn className="btnDefault noFloat" onClick={this.handleCreate} style={{ marginRight: 10 }}>
                Novo produto
              </MDBBtn>
            }

            <Button
              icon="export"
              className="btnRoxo"
              onClick={() => this.handleExport()}
              loading={this.obs.loading_export}
              style={{ marginRight: 10 }}
            >
              Exportar
            </Button>

            {user_admin &&
              <Button
                icon="import"
                className="btnRoxo"
                onClick={() => this.handleImport()}
                loading={this.obs.loading_import}
              >
                Importar
              </Button>
            }

          </MDBCol>
        </MDBRow>

        <MDBRow>
          <MDBCol md="6" className="inputFilter">
            <InputGroup
              round
              leftIcon="search"
              style={{ width: 400, borderRadius: 10 }}
              placeholder="Filtrar..."
              value={ProdutoStore.obs.filter}
              onChange={(e) => this.handleSetFilter(e)}
            />
          </MDBCol>
        </MDBRow>

        <Tabs
          id="tabs"
          className={"tabsCalculos"}
          selectedTabId={ProdutoStore.obs.selectedTab}
          onChange={(tabId) => this.handleChangeTab(String(tabId))}
        >

          <Tab
            id="entrada"
            title="Entrada"
            panel={<></>} />

          <Tab
            id="saida"
            title="Saída"
            panel={<></>} />

        </Tabs>

        <div className="TableDefault">

          <HTMLTable
            className="table table-hover dataTable"
            style={{ width: "100%", marginTop: 15 }}
            small
          >
            <thead>
              <tr>
                <th style={{ width: 120 }}>CEST</th>
                <th style={{ width: 120 }}>NCM</th>
                <th>Descrição</th>
                <th style={{ width: 150 }}>Valido de</th>
                <th style={{ width: 150 }}>Valido até</th>
                {user_admin && <th style={{ width: 150 }} />}
              </tr>
            </thead>
            <tbody>
              {items}
            </tbody>
          </HTMLTable>

        </div>

        <div style={{ marginTop: 5, float: "right", marginBottom: 120 }}>

          <Pager
            currentPage={this.obs.currentPage}
            totalCount={ProdutoStore.obs.produtos_length}
            pageSize={50}
            onPageChange={page => this.handleChangePage(page)}
          />

        </div>

        <Dialog
          isOpen={this.obs.open_import_produtos}
          title={"Importar Produtos - " + ProdutoStore.obs.selectedTab.toUpperCase()}
        >
          <div className={Classes.DIALOG_BODY}>

            <p style={{ color: "rgba(255, 0, 0, 0.82)" }}>

              <b>Observação:</b> Ao importar o arquivo, todos os produtos cadastrados no sistema serão substituídos pelos produtos presentes no arquivo, conforme o tipo selecionado na aba. <br />

            </p>

            <br />

            <FormGroup label="">
              <FileInput text={this.obs.file_name} className="bp3-fill bp3-file-input-has-selection input-file-certificado" onInputChange={this.handleFileInputChange} disabled={this.obs.loading_import} />
            </FormGroup>
          </div>

          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button text="Fechar" intent="primary" onClick={() => this.obs.open_import_produtos = false} />
            </div>
          </div>

        </Dialog>

        {this.obs.viewing && (<ProdutoDialog estado={this.props.estado} produto={this.obs.viewing} onClose={this.handleViewClose} />)}
        {this.obs.create && (<ProdutoDialogCreate estado={this.props.estado} onClose={() => this.obs.create = false} />)}

      </div>
    );
  }
}

export default ProdutosPage;
